import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { App as CapApp } from '@capacitor/app';

// Android hardware back button handling (Capacitor only — a no-op in the
// browser). Behaviour mirrors the original Android app:
//   • anywhere but the Bill screen → navigate back through history
//   • on the Bill screen (root) → press back twice within 2s to exit
const EXIT_WINDOW_MS = 2000;

const BackButtonHandler: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showExitHint, setShowExitHint] = useState(false);
  const lastBackRef = useRef(0);
  // Keep current values in refs so the single listener sees fresh state.
  const pathRef = useRef(location.pathname);
  pathRef.current = location.pathname;

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    const sub = CapApp.addListener('backButton', ({ canGoBack }) => {
      const atRoot = pathRef.current === '/';
      if (!atRoot && (canGoBack || window.history.length > 1)) {
        navigate(-1);
        return;
      }
      const now = Date.now();
      if (now - lastBackRef.current < EXIT_WINDOW_MS) {
        CapApp.exitApp();
      } else {
        lastBackRef.current = now;
        setShowExitHint(true);
        setTimeout(() => setShowExitHint(false), EXIT_WINDOW_MS);
      }
    });

    return () => {
      sub.then((s) => s.remove());
    };
  }, [navigate]);

  if (!showExitHint) return null;
  return (
    <div
      style={{
        position: 'fixed',
        bottom: 24,
        left: '50%',
        transform: 'translateX(-50%)',
        background: 'rgba(0,0,0,0.8)',
        color: '#fff',
        padding: '10px 18px',
        borderRadius: 20,
        fontSize: 14,
        zIndex: 9999,
        pointerEvents: 'none',
      }}
    >
      Press back again to exit
    </div>
  );
};

export default BackButtonHandler;
